import { HybridspaceError } from 'hybridspace-common/lib/components/HybridspaceError';
import { placesFwkBootPtsCompleteAction } from 'hybridspace-performance-datapoints';
import { logAppBootStep } from 'hybridspace-telemetry/lib/logAppBootStep';
import { isRunningInMetaOSHub } from 'owa-config';
import {
    lazyInitializeMetaOsAppSdk,
    lazyNotifyAppLoaded,
    lazyNotifySuccess,
} from 'owa-metaos-app-bootstrap';
import { observer } from 'owa-mobx-react';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { markEnd, markStart } from 'owa-performance';
import { usePlacesSettings } from 'places-settings';
import { setModuleStateSafe } from 'places-user-session-stores';
import React, { useEffect, useRef } from 'react';
import AccessDenied from './AccessDenied';
import { HybridspaceLoadingScreen } from './HybridspaceLoadingScreen';
import {
    unableToLoadPlacesSettings,
    unableToLoadPlacesSettingsButtonLabel,
    unableToLoadPlacesSettingsTitle,
} from './PlacesPermissionValidation.locstring.json';

import type { PlacesSettings } from 'places-settings';

// Actually, the "initialize" step should already be done in the "owa-start" package.
// packages/framework/owa-start/src/getRunBeforeStartForHosted.ts
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function isRunningInMetaOSHub called at module scope is not allowed. This may cause unwanted side effects. */
const initializeMetaAppSdkPromise = isRunningInMetaOSHub()
    ? /* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
       * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
       *	> Function importAndExecute called at module scope is not allowed. This may cause unwanted side effects. */
      lazyInitializeMetaOsAppSdk.importAndExecute()
    : undefined;

const defaultExport: React.FunctionComponent<
    React.PropsWithChildren<{
        hostApp: string;
    }>
> = observer(function PlacesPermissionValidation({
    hostApp,
    children,
}: React.PropsWithChildren<{ hostApp: string }>) {
    useEffect(() => {
        markStart('PlacesPermissionValidation');
    }, []);

    const mailboxInfo = getModuleContextMailboxInfo();
    const { loading, failed, noAccess, retry } = usePlacesSettings(
        mailboxInfo,
        true /* shouldUseMainThread */,
        onPlacesTenantSettingComplete
    );

    useEffect(() => {
        if (!loading) {
            // this is used to remove the custom loading screen once we completed places tenant settings
            setModuleStateSafe('complete');
        }
    }, [loading]);

    const isFirstLoaded = useRef(true);

    useEffect(() => {
        if (!loading && isFirstLoaded.current) {
            // don't want to run this on every retry
            isFirstLoaded.current = false;

            // it doesn't take places tenant setting success or failure into account as Places app would handle that user experience
            // we are only using this to let the host app know that we are in a good state to proceed, and to avoid timed out from hub.
            const promise = initializeMetaAppSdkPromise?.then(async () => {
                markStart('PlacesNotifyAppLoaded');
                await Promise.allSettled([
                    lazyNotifyAppLoaded.importAndExecute(),
                    lazyNotifySuccess.importAndExecute(),
                ]);
                markEnd('PlacesNotifyAppLoaded');
            });

            (promise ?? Promise.resolve()).then(() => {
                markEnd('PlacesPermissionValidation');
                logAppBootStep('AppBootEnd', { hasError: failed });
            });
        }
    }, [failed, loading]);

    if (loading) {
        return <HybridspaceLoadingScreen />;
    }

    if (failed) {
        return (
            <HybridspaceError
                retry={retry}
                buttonLabel={unableToLoadPlacesSettingsButtonLabel}
                errorTitle={unableToLoadPlacesSettingsTitle}
                errorMessage={unableToLoadPlacesSettings}
                brandedButtonStyle={true}
                dataTelemetryId={'PlacesPermissionValidation'}
            />
        );
    }

    if (noAccess) {
        return <AccessDenied showSignOut={!hostApp} />;
    }

    return children;
}, 'PlacesPermissionValidation');
export default defaultExport;

function onPlacesTenantSettingComplete(placesSettings: PlacesSettings) {
    placesFwkBootPtsCompleteAction(
        placesSettings.placesWebAppEnabled,
        placesSettings.placesPremiumEnabled,
        placesSettings.reason,
        placesSettings.isOffline
    );
}
